import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { Select } from '@/components/Select';
import { StatusPill } from '@/components/StatusPill';
import {
  TabBody, TabToolbar, TableCard, Th, Td, Tr, SkeletonCards, InlineHint, MonoCopyCard,
} from '@/components/tiktok/measurementShared';
import { tiktokService } from '@/services/tiktok.service';
import { useTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';
import { tiktokEventsUrl } from '@/lib/tiktokLinks';
import { Activity, Send, RefreshCw, Plus, ChevronDown, ChevronUp, ExternalLink, Code2 } from 'lucide-react';
import toast from 'react-hot-toast';

// TikTok's `pixel_category` (Pixel API v1.3+) is a site-type, not an industry.
// Allowed values: CONTACTS, CUSTOMIZE_EVENTS, FILLING_FORM, LANDING_PAGE, ONLINE_STORE.
const PIXEL_CATEGORIES: Array<{ value: string; label: string; hint: string }> = [
  { value: 'LANDING_PAGE',     label: 'Landing page',      hint: 'Marketing / info site. Default for most use cases.' },
  { value: 'ONLINE_STORE',     label: 'Online store',      hint: 'E-commerce — purchases, add-to-cart, product views.' },
  { value: 'FILLING_FORM',     label: 'Form submissions',  hint: 'Site whose primary conversion is a form submit.' },
  { value: 'CONTACTS',         label: 'Contact collection', hint: 'Lead-gen / phone or email signup.' },
  { value: 'CUSTOMIZE_EVENTS', label: 'Custom events',     hint: 'Fully custom event taxonomy.' },
];

/**
 * Shape returned by GET /tiktok/pixels — the backend normalises TikTok's
 * snake_case into this and adds the configured events (needed before an ad
 * group can optimise for a conversion).
 */
interface PixelRow {
  pixelId: string;
  pixelCode: string;
  pixelName?: string;
  events?: Array<{ eventType: string; label?: string }>;
}

/** TikTok's standard web events, usable as an ad group's optimisation event. */
const PIXEL_EVENT_TYPES = [
  { value: 'ON_WEB_ORDER',      label: 'Place an order' },
  { value: 'ON_WEB_CART',       label: 'Add to cart' },
  { value: 'ON_WEB_DETAIL',     label: 'View content' },
  { value: 'ON_WEB_REGISTER',   label: 'Complete registration' },
  { value: 'ON_WEB_SUBSCRIBE',  label: 'Subscribe' },
  { value: 'FORM',              label: 'Submit form' },
  { value: 'CONSULT',           label: 'Contact / consult' },
  { value: 'BUTTON',            label: 'Click button' },
];

const COMMON_EVENTS = [
  'CompletePayment',
  'PlaceAnOrder',
  'AddToCart',
  'ViewContent',
  'ClickButton',
  'CompleteRegistration',
  'Search',
  'AddToWishlist',
  'Subscribe',
  'Contact',
];

/** The standard TikTok Pixel base code, with the pixel code inserted. */
function installSnippet(pixelCode: string): string {
  return [
    '<script>',
    '!function (w, d, t) {',
    '  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};',
    `  ttq.load('${pixelCode}');`,
    '  ttq.page();',
    "}(window, document, 'ttq');",
    '</script>',
  ].join('\n');
}

type Props = {
  /** Rendered inside a TabbedPage shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function TiktokPixel({ embedded = false }: Props = {}) {
  const [pixels, setPixels] = useState<PixelRow[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [openPixelId, setOpenPixelId] = useState<string | null>(null);
  const { account } = useTiktokAccountCurrency();

  const [testOpen, setTestOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [eventPixelId, setEventPixelId] = useState('');
  const [newEventType, setNewEventType] = useState('ON_WEB_ORDER');
  const [addingEvent, setAddingEvent] = useState(false);
  const [pixelCode, setPixelCode] = useState('');
  const [event, setEvent] = useState('CompletePayment');
  const [eventId, setEventId] = useState('');
  const [busy, setBusy] = useState(false);

  // Create-pixel modal state
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('LANDING_PAGE');
  const [creating, setCreating] = useState(false);

  const refresh = async (announce = false) => {
    setRefreshing(true);
    try {
      const r = (await tiktokService.listPixels()) as PixelRow[];
      setPixels(r);
      if (announce) toast.success(`${r.length} pixel${r.length === 1 ? '' : 's'} loaded`);
    } catch {
      setPixels([]);
    } finally { setRefreshing(false); }
  };

  useEffect(() => { refresh(); }, []);

  const createPixel = async () => {
    if (!newName.trim()) { toast.error('Pixel name is required'); return; }
    setCreating(true);
    try {
      const r = await tiktokService.createPixel({ name: newName.trim(), category: newCategory });
      toast.success(`Pixel created · code ${r.pixelCode}`);
      setCreateOpen(false);
      setNewName('');
      setNewCategory('LANDING_PAGE');
      await refresh();
      setOpenPixelId(r.pixelId);
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setCreating(false); }
  };

  // When the modal opens, auto-generate a fresh event_id for dedup so users
  // don't have to invent one. They can still overwrite it.
  // Also default to the first pixel when none is selected.
  useEffect(() => {
    if (!testOpen) return;
    if (!eventId) {
      const fresh = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `evt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      setEventId(fresh);
    }
    if (!pixelCode && pixels && pixels.length > 0) {
      setPixelCode(pixels[0].pixelCode || pixels[0].pixelId || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testOpen]);

  const addPixelEvent = async () => {
    if (!eventPixelId) { toast.error('No pixel selected'); return; }
    setAddingEvent(true);
    try {
      await tiktokService.createPixelEvent(eventPixelId, { eventType: newEventType });
      toast.success(`Event ${newEventType} added`);
      setAddEventOpen(false);
      await refresh();
    } catch {
      // axios interceptor toasts the backend message
    } finally { setAddingEvent(false); }
  };

  const sendTestEvent = async () => {
    if (!pixelCode || !event || !eventId) { toast.error('Pixel code, event name, and event ID are required'); return; }
    setBusy(true);
    try {
      const r = await tiktokService.sendPixelEvent(pixelCode, {
        event,
        event_id: eventId,
        event_time: Math.floor(Date.now() / 1000),
        properties: { source: 'slightercrm_test' },
      });
      if (r.ok) toast.success('Event sent — appears in TikTok Events Manager within minutes');
      else toast.error(r.message ?? 'Send failed');
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setBusy(false); }
  };

  const openAddEvent = (p: PixelRow) => { setEventPixelId(p.pixelId); setAddEventOpen(true); };
  const openTest = (p: PixelRow) => { setPixelCode(p.pixelCode || p.pixelId || ''); setTestOpen(true); };

  const selected = useMemo(
    () => (pixels ?? []).find((p) => (p.pixelId || p.pixelCode) === openPixelId) ?? null,
    [pixels, openPixelId],
  );

  const loading = pixels === null;
  const hasData = !!pixels && pixels.length > 0;
  const withEvents = (pixels ?? []).filter((p) => (p.events ?? []).length > 0).length;

  const actions = (
    <>
      <Button variant="secondary" onClick={() => refresh(true)} loading={refreshing}><RefreshCw size={14} /> Refresh</Button>
      <Button variant="secondary" onClick={() => setTestOpen(true)}><Send size={14} /> Send test event</Button>
      <Button variant="primary" onClick={() => setCreateOpen(true)}><Plus size={14} /> Create pixel</Button>
    </>
  );

  const eventMeta = (type: string) => PIXEL_EVENT_TYPES.find((t) => t.value === type);

  return (
    <>
      {!embedded && (
        <Topbar
          title="Pixel"
          breadcrumb="TikTok Ads / Pixel"
          description="Pixels track conversions on your site. Server-side events can be sent manually; automatic CRM conversion forwarding is not active yet."
        />
      )}

      <TabBody>
        <TabToolbar
          title="Pixels"
          count={pixels?.length ?? null}
          subtitle={hasData
            ? `${withEvents} of ${pixels!.length} ready for conversion campaigns · an ad group can only optimise for an event that already exists on its pixel.`
            : 'Pixels track conversions on your site. Automatic CRM conversion forwarding is not active yet.'}
          right={actions}
        />

        {loading && <SkeletonCards count={3} />}

        {!loading && !hasData && (
          <EmptyState
            icon={<Activity size={32} />}
            title="No pixels yet"
            description="Create one here (it calls TikTok's pixel/create) or build it in TikTok Events Manager and refresh. Add at least one event afterwards — conversion campaigns need it."
            action={
              <>
                <Button variant="primary" onClick={() => setCreateOpen(true)}><Plus size={14} /> Create pixel</Button>
                <Button variant="secondary" onClick={() => window.open(tiktokEventsUrl(account?.advertiserId), '_blank', 'noopener')}>
                  <ExternalLink size={14} /> Events Manager
                </Button>
              </>
            }
          />
        )}

        {hasData && (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {pixels!.map((p, i) => {
              const key = p.pixelId || p.pixelCode || String(i);
              const count = (p.events ?? []).length;
              const open = openPixelId === key;
              return (
                <Card
                  key={key}
                  hoverable
                  className={clsx('flex flex-col gap-4 !p-4 transition-colors', open && 'border-primary')}
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                      <Activity size={18} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-text-primary">{p.pixelName || p.pixelId}</div>
                      <div className="mt-0.5 truncate font-mono text-2xs text-text-muted" title={`Code ${p.pixelCode} · ID ${p.pixelId}`}>
                        {p.pixelCode || p.pixelId}{p.pixelCode && p.pixelId && p.pixelCode !== p.pixelId ? ` · id ${p.pixelId}` : ''}
                      </div>
                    </div>
                    {count > 0
                      ? <StatusPill status="ready" label="Ready" variant="success" />
                      : <StatusPill status="no-events" label="No events" variant="warning" />}
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-muted">
                      <span className="font-semibold tabular-nums text-text-primary">{count}</span> {count === 1 ? 'event' : 'events'} configured
                    </span>
                    <button
                      type="button"
                      onClick={() => setOpenPixelId(open ? null : key)}
                      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                    >
                      {open ? 'Hide details' : 'Details'} {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  </div>

                  <div className="flex items-center gap-2 border-t border-border pt-3">
                    <Button size="sm" variant="secondary" onClick={() => openAddEvent(p)}><Plus size={12} /> Add event</Button>
                    <Button size="sm" variant="ghost" onClick={() => openTest(p)}><Send size={12} /> Test event</Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {selected && (
          <Card>
            <SectionHeader
              icon={<Activity size={16} />}
              title={selected.pixelName || selected.pixelId}
              subtitle={`Code ${selected.pixelCode || '—'} · ID ${selected.pixelId || '—'}`}
              action={
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="secondary" onClick={() => window.open(tiktokEventsUrl(account?.advertiserId), '_blank', 'noopener')}>
                    <ExternalLink size={12} /> Events Manager
                  </Button>
                  <Button size="sm" variant="primary" onClick={() => openAddEvent(selected)}><Plus size={12} /> Add event</Button>
                </div>
              }
            />

            <div className="grid gap-5 xl:grid-cols-5">
              <div className="space-y-3 xl:col-span-3">
                <h4 className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">Events</h4>
                {(selected.events ?? []).length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border bg-surface-alt/40 px-4 py-8 text-center">
                    <div className="text-sm font-medium text-text-primary">No events on this pixel</div>
                    <div className="mt-1 text-xs text-text-muted">Conversion campaigns need at least one — add a standard web event.</div>
                    <Button size="sm" variant="secondary" className="mt-3" onClick={() => openAddEvent(selected)}><Plus size={12} /> Add event</Button>
                  </div>
                ) : (
                  <TableCard minWidth={420} maxHeight={320}>
                    <thead>
                      <tr>
                        <Th>Event</Th>
                        <Th>Type</Th>
                        <Th>Code</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selected.events ?? []).map((e) => {
                        const meta = eventMeta(e.eventType);
                        return (
                          <Tr key={e.eventType}>
                            <Td className="font-medium">{e.label || meta?.label || e.eventType}</Td>
                            <Td>
                              <StatusPill
                                status={meta ? 'standard' : 'custom'}
                                label={meta ? 'Standard' : 'Custom'}
                                variant={meta ? 'info' : 'neutral'}
                              />
                            </Td>
                            <Td mono muted>{e.eventType}</Td>
                          </Tr>
                        );
                      })}
                    </tbody>
                  </TableCard>
                )}
                <InlineHint tone="warning">
                  Event currency isn't set from here — TikTok only accepts USD, JPY or INR on pixel events, so it's omitted and values are reported without a currency.
                </InlineHint>
              </div>

              <div className="space-y-3 xl:col-span-2">
                <h4 className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
                  <Code2 size={12} /> Install snippet
                </h4>
                <MonoCopyCard
                  title="Paste before </head>"
                  code={installSnippet(selected.pixelCode || selected.pixelId)}
                  rows={9}
                  hint={<InlineHint>Loads the pixel and fires a PageView on every page. Automatic CRM conversion forwarding is not active in this release.</InlineHint>}
                />
              </div>
            </div>
          </Card>
        )}
      </TabBody>

      <Modal open={createOpen} onClose={() => !creating && setCreateOpen(false)} title="Create pixel" widthClass="max-w-md">
        <div className="space-y-1">
          <p className="mb-4 text-xs leading-relaxed text-text-muted">
            TikTok returns a <code className="rounded bg-surface-alt px-1 font-mono">pixel_code</code> on creation — paste the install snippet into your site's head.
          </p>
          <FormField label="Pixel name" required>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Main website pixel" autoFocus />
          </FormField>
          <FormField
            label="Category"
            required
            hintBelow hint={PIXEL_CATEGORIES.find((c) => c.value === newCategory)?.hint ?? 'Used by TikTok for event-suggestion defaults.'}
          >
            <Select value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {PIXEL_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
          </FormField>
        </div>
        <div className="-mx-4 -mb-4 flex justify-end gap-2 border-t border-border px-4 py-4 sm:-mx-6 sm:-mb-5 sm:px-6">
          <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="primary" onClick={createPixel} loading={creating} disabled={creating || !newName.trim()}>
            <Plus size={14} /> Create
          </Button>
        </div>
      </Modal>

      <Modal open={testOpen} onClose={() => !busy && setTestOpen(false)} title="Send server-side event" widthClass="max-w-lg">
        <div className="space-y-1">
          <p className="mb-4 text-xs leading-relaxed text-text-muted">
            Sends one conversion event to TikTok's Events API now. CRM events are not forwarded automatically in this release.
          </p>
          <FormField label="Pixel" required>
            {pixels && pixels.length > 0 ? (
              <Select value={pixelCode} onChange={(e) => setPixelCode(e.target.value)}>
                <option value="">— Pick a pixel —</option>
                {pixels.map((p, i) => {
                  const code = p.pixelCode || p.pixelId || '';
                  return (
                    <option key={code || i} value={code}>
                      {p.pixelName || code}{p.pixelName ? ` · ${code}` : ''}
                    </option>
                  );
                })}
              </Select>
            ) : (
              <Input
                value={pixelCode}
                onChange={(e) => setPixelCode(e.target.value)}
                placeholder="C1234ABCDEFGHIJK"
              />
            )}
          </FormField>
          <FormField label="Event name" required>
            <Select value={event} onChange={(e) => setEvent(e.target.value)}>
              {COMMON_EVENTS.map((e) => <option key={e} value={e}>{e}</option>)}
            </Select>
          </FormField>
          <FormField label="Event ID (deduplication)" required hintBelow hint="Unique per event — TikTok uses this to dedupe with the browser pixel.">
            <Input value={eventId} onChange={(e) => setEventId(e.target.value)} placeholder="evt_abc123" className="font-mono" />
          </FormField>
        </div>
        <div className="-mx-4 -mb-4 flex justify-end gap-2 border-t border-border px-4 py-4 sm:-mx-6 sm:-mb-5 sm:px-6">
          <Button variant="secondary" onClick={() => setTestOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={sendTestEvent} loading={busy} disabled={busy}>
            <Send size={14} /> Send
          </Button>
        </div>
      </Modal>

      <Modal open={addEventOpen} onClose={() => !addingEvent && setAddEventOpen(false)} title="Add a pixel event" widthClass="max-w-md">
        <div className="space-y-1">
          <p className="mb-4 text-xs leading-relaxed text-text-muted">
            Conversion campaigns optimise towards an event that already exists on the pixel. Adding one here is the same as creating a web event in TikTok Events Manager.
          </p>
          <FormField label="Event type" required hintBelow hint="TikTok's standard web events.">
            <Select value={newEventType} onChange={(e) => setNewEventType(e.target.value)}>
              {PIXEL_EVENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label} ({t.value})</option>
              ))}
            </Select>
          </FormField>
          <InlineHint tone="warning">Currency is omitted on purpose — TikTok only accepts USD, JPY or INR here.</InlineHint>
        </div>
        <div className="-mx-4 -mb-4 mt-4 flex justify-end gap-2 border-t border-border px-4 py-4 sm:-mx-6 sm:-mb-5 sm:px-6">
          <Button variant="secondary" onClick={() => setAddEventOpen(false)} disabled={addingEvent}>Cancel</Button>
          <Button variant="primary" onClick={addPixelEvent} loading={addingEvent}><Plus size={14} /> Add event</Button>
        </div>
      </Modal>
    </>
  );
}
